import { useCallback, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { UploadProps } from 'antd';

import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    getApiErrorMessage,
    uploadCloudAttachment,
} from '@/services';
import { nanoId } from '@/utilities';

export type AttachmentUploadStatus = 'queued' | 'uploading' | 'processing' | 'error';

export interface AttachmentUploadItem {
    error?: string;
    fileName: string;
    id: string;
    mimeType: string;
    progress: number;
    relativePath?: string;
    size: number;
    status: AttachmentUploadStatus;
}

export interface AttachmentUploadBatchProgress {
    batchId: string;
    canceled: number;
    completed: number;
    done: boolean;
    failed: number;
    settled: number;
    total: number;
}

type AttachmentUploadOutcome = 'canceled' | 'completed' | 'failed';

interface AttachmentUploadBatch {
    batchId: string;
    canceled: number;
    completed: number;
    failed: number;
    sealed: boolean;
    total: number;
}

interface PendingAttachmentUpload {
    batchId: string;
    controller: AbortController;
    file: File;
    id: string;
    onError?: (error: Error) => void;
    onProgress?: (event: { percent: number }) => void;
    onSuccess?: (response: CloudAttachment) => void;
    settled?: boolean;
}

interface UseAttachmentBatchUploadOptions {
    concurrency?: number;
    connection: CloudStorageConnection;
    destination: AttachmentDestination;
    onBatchProgress?: (progress: AttachmentUploadBatchProgress) => void;
    onUploaded: (attachment: CloudAttachment) => void;
}

const isBrowserFile = (file: string | Blob): file is File => typeof file !== 'string' && 'name' in file;

export const useAttachmentBatchUpload = ({
    concurrency = 3,
    connection,
    destination,
    onBatchProgress,
    onUploaded,
}: UseAttachmentBatchUploadOptions) => {
    const [items, setItems] = useState<AttachmentUploadItem[]>([]);
    const activeCountRef = useRef(0);
    const batchesRef = useRef(new Map<string, AttachmentUploadBatch>());
    const currentBatchIdRef = useRef<string>();
    const batchSealTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const processQueueRef = useRef<() => void>(() => undefined);
    const queueRef = useRef<PendingAttachmentUpload[]>([]);
    const controllersRef = useRef(new Map<string, AbortController>());
    const requestsRef = useRef(new Map<string, PendingAttachmentUpload>());

    const notifyBatch = useCallback(
        (batch: AttachmentUploadBatch) => {
            const settled = batch.completed + batch.failed + batch.canceled;
            const done = batch.sealed && settled >= batch.total;

            onBatchProgress?.({
                batchId: batch.batchId,
                canceled: batch.canceled,
                completed: batch.completed,
                done,
                failed: batch.failed,
                settled,
                total: batch.total,
            });

            if (done) {
                batchesRef.current.delete(batch.batchId);
            }
        },
        [onBatchProgress],
    );

    const registerBatchItem = useCallback(() => {
        let batchId = currentBatchIdRef.current;
        let batch = batchId ? batchesRef.current.get(batchId) : undefined;

        if (!batch) {
            batchId = nanoId(16);
            batch = { batchId, canceled: 0, completed: 0, failed: 0, sealed: false, total: 0 };
            batchesRef.current.set(batchId, batch);
            currentBatchIdRef.current = batchId;
        }

        batch.total += 1;
        clearTimeout(batchSealTimerRef.current);
        batchSealTimerRef.current = setTimeout(() => {
            const activeBatchId = currentBatchIdRef.current;
            const activeBatch = activeBatchId ? batchesRef.current.get(activeBatchId) : undefined;

            currentBatchIdRef.current = undefined;
            if (activeBatch) {
                activeBatch.sealed = true;
                notifyBatch(activeBatch);
            }
        }, 0);

        return batch.batchId;
    }, [notifyBatch]);

    const settlePending = useCallback(
        (pending: PendingAttachmentUpload, outcome: AttachmentUploadOutcome) => {
            if (pending.settled) {
                return;
            }

            pending.settled = true;
            const batch = batchesRef.current.get(pending.batchId);

            if (!batch) {
                return;
            }

            batch[outcome] += 1;
            notifyBatch(batch);
        },
        [notifyBatch],
    );

    const updateItem = useCallback((id: string, updates: Partial<AttachmentUploadItem>) => {
        setItems((current) => current.map((item) => (item.id === id ? { ...item, ...updates } : item)));
    }, []);

    const removeItem = useCallback((id: string) => {
        setItems((current) => current.filter((item) => item.id !== id));
        controllersRef.current.delete(id);
        requestsRef.current.delete(id);
    }, []);

    const cancel = useCallback(
        (id: string) => {
            const pending = requestsRef.current.get(id);

            pending?.controller.abort();
            queueRef.current = queueRef.current.filter((pending) => pending.id !== id);
            if (pending) {
                settlePending(pending, 'canceled');
            }
            removeItem(id);
            processQueueRef.current();
        },
        [removeItem, settlePending],
    );

    const uploadOne = useCallback(
        async (pending: PendingAttachmentUpload) => {
            updateItem(pending.id, { status: 'uploading' });

            try {
                const attachment = await uploadCloudAttachment({
                    connection,
                    destination,
                    file: pending.file,
                    signal: pending.controller.signal,
                    onUploadProgress: ({ loaded, total }) => {
                        const progress = total ? Math.min(Math.round((loaded / total) * 100), 100) : 0;

                        updateItem(pending.id, {
                            progress,
                            status: progress >= 100 ? 'processing' : 'uploading',
                        });
                        pending.onProgress?.({ percent: progress });
                    },
                    uploadId: pending.id,
                });

                onUploaded(attachment);
                pending.onSuccess?.(attachment);
                settlePending(pending, 'completed');
                removeItem(pending.id);
            } catch (error) {
                if (pending.controller.signal.aborted) {
                    settlePending(pending, 'canceled');
                    removeItem(pending.id);
                    return;
                }

                captureException(error, {
                    extra: {
                        attachmentName: pending.file.name,
                        resourceId: destination.resourceId,
                        resourceType: destination.resourceType,
                    },
                });

                const message = getApiErrorMessage(error, 'Attachment upload failed.');
                const normalizedError = error instanceof Error ? error : new Error(message);

                updateItem(pending.id, { error: message, status: 'error' });
                pending.onError?.(normalizedError);
                settlePending(pending, 'failed');
            } finally {
                activeCountRef.current = Math.max(activeCountRef.current - 1, 0);
                processQueueRef.current();
            }
        },
        [connection, destination, onUploaded, removeItem, settlePending, updateItem],
    );

    const processQueue = useCallback(() => {
        while (activeCountRef.current < concurrency && queueRef.current.length > 0) {
            const pending = queueRef.current.shift();

            if (!pending) {
                return;
            }

            activeCountRef.current += 1;
            void uploadOne(pending);
        }
    }, [concurrency, uploadOne]);

    processQueueRef.current = processQueue;

    const enqueue: NonNullable<UploadProps['customRequest']> = useCallback(
        ({ file, onError, onProgress, onSuccess }) => {
            if (!isBrowserFile(file)) {
                onError?.(new Error('Only browser files can be uploaded.'));
                return;
            }

            const id = nanoId(20);
            const batchId = registerBatchItem();
            const controller = new AbortController();

            controllersRef.current.set(id, controller);
            setItems((current) => [
                ...current,
                {
                    fileName: file.name,
                    id,
                    mimeType: file.type || 'application/octet-stream',
                    progress: 0,
                    relativePath: file.webkitRelativePath,
                    size: file.size,
                    status: 'queued',
                },
            ]);
            const pending = {
                batchId,
                controller,
                file,
                id,
                onError,
                onProgress,
                onSuccess,
            };

            requestsRef.current.set(id, pending);
            queueRef.current.push(pending);
            processQueueRef.current();

            return {
                abort: () => cancel(id),
            };
        },
        [cancel, registerBatchItem],
    );

    const retry = useCallback(
        (id: string) => {
            const failedItem = items.find((item) => item.id === id);
            const previousRequest = requestsRef.current.get(id);

            if (!failedItem || !previousRequest || failedItem.status !== 'error') {
                return;
            }

            const controller = new AbortController();
            const pending = {
                ...previousRequest,
                batchId: registerBatchItem(),
                controller,
                settled: false,
            };

            updateItem(id, { error: undefined, progress: 0, status: 'queued' });
            controllersRef.current.set(id, controller);
            requestsRef.current.set(id, pending);
            queueRef.current.push(pending);
            processQueueRef.current();
        },
        [items, registerBatchItem, updateItem],
    );

    useEffect(() => {
        const controllers = controllersRef.current;
        const requests = requestsRef.current;

        return () => {
            controllers.forEach((controller) => controller.abort());
            clearTimeout(batchSealTimerRef.current);
            controllers.clear();
            requests.clear();
            queueRef.current = [];
            batchesRef.current.clear();
            currentBatchIdRef.current = undefined;
        };
    }, [connection.id, destination.resourceId, destination.recordName]);

    return { cancel, customRequest: enqueue, items, retry };
};
